import React, { useState, useRef, useEffect } from 'react';
import axios from "axios";
import './SorterPage.css';

const SorterPage = () => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [results, setResults] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const fileInputRef = useRef(null);
  const resultsRef = useRef(null);

  // Safe localStorage helper to handle quota errors
  const safeSetItem = (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing old data...');
        // Clear old data and try again
        try {
          localStorage.removeItem('sorter_selectedImages');
          localStorage.removeItem('sorter_results');
          localStorage.setItem(key, value);
          return true;
        } catch (retryError) {
          console.error('localStorage still full after cleanup:', retryError);
          return false;
        }
      }
      console.error('Error saving to localStorage:', error);
      return false;
    }
  };

  // Load state from localStorage on mount  
  useEffect(() => {
    try {
      const savedImages = localStorage.getItem('sorter_selectedImages');
      const savedResults = localStorage.getItem('sorter_results');
      
      // Load images if available
      if (savedImages) {
        setSelectedImages(JSON.parse(savedImages));
      }
      
      // Load results independently (queue persists even without images)
      if (savedResults) {
        setResults(JSON.parse(savedResults));
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
      // Clear corrupted data
      localStorage.removeItem('sorter_results');
      localStorage.removeItem('sorter_selectedImages');
    }
  }, []);

  // Save selectedImages to localStorage
  useEffect(() => {
    if (selectedImages.length > 0) {
      try {
        safeSetItem('sorter_selectedImages', JSON.stringify(selectedImages));
      } catch (error) {
        console.error('Error saving images:', error);
      }
    } else {
      try {
        localStorage.removeItem('sorter_selectedImages');
        // Don't clear results when images are cleared - results queue persists
      } catch (error) {
        console.error('Error removing data:', error);
      }
    }
  }, [selectedImages]);

  // Save results to localStorage
  useEffect(() => {
    if (results.length > 0) {
      try {
        // Save full results including images
        safeSetItem('sorter_results', JSON.stringify(results));
      } catch (error) {
        console.error('Error saving results:', error);
      }
    } else {
      try {
        localStorage.removeItem('sorter_results');
      } catch (error) {
        console.error('Error removing results:', error);
      }
    }
  }, [results]);

  // Handle drop zone click
  const handleDropZoneClick = () => {
    fileInputRef.current.click();
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    handleFilesSelection(files);
  };

  // Process selected files
  const handleFilesSelection = (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImages(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove a specific image
  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all images
  const handleClearAll = () => {
    setSelectedImages([]);
    setResults([]);
  };

  // Process images and generate mock results
  // ================= REAL AI API PROCESS =================
const handleProcessImages = async () => {
  try {
    console.log("Processing images...", selectedImages.length);

    if (selectedImages.length === 0) {
      alert("No images to process. Please upload images first.");
      return;
    }

    const form = new FormData();

    selectedImages.forEach(img => {
      if (img.file) {
        form.append("images", img.file); // ONLY File object
      }
    });


    form.append("company_id", "123"); // optional

    console.log("Uploading to AI backend...");

    const response = await axios.post(
      "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/autofill/analyze",
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );

    console.log("AI RESPONSE:", response.data);

    const data = response.data;

    // MAP API JSON → UI TABLE FORMAT
    const result = {
      image: selectedImages[0].preview,
      fileName: selectedImages[0].file?.name || "Uploaded Image",
      description: data?.product_metadata?.title || "Unknown Product",
      price: data?.price?.recommended || 0,
      authScore: data?.authenticity?.score || 0,
      conditionScore: data?.condition?.score || 0,
      suggestion: data?.thriftops_suggestion?.channel || "N/A",

      authReasons: [
        { status: "pass", text: data?.authenticity?.summary || "No details" }
      ],
      conditionReasons: [
        { status: "pass", text: data?.condition?.summary || "No details" }
      ]
    };

    // Add ONLY one row per batch (same behavior as mock)
    setResults(prev => [...prev, result]);

    // Clear uploaded images
    setSelectedImages([]);

    // Scroll to results
    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    }, 100);

  } catch (error) {
    console.error("API ERROR:", error);
    alert("AI API failed. Check console logs.");
  }
};


  // Handle action buttons
  const handleSellOnline = (index) => {
    alert(`Redirecting to online listing for: ${results[index].description}`);
  };

  const handlePushToRetail = (index) => {
    alert(`Pushing to retail for: ${results[index].description}`);
  };

  const handleSellWholesale = (index) => {
    alert(`Processing wholesale for: ${results[index].description}`);
  };

  // Handle modal open for score reasons
  const openReasonModal = (type, score, reasons, description) => {
    setModalData({
      type,
      score,
      reasons: reasons || [],
      description
    });
    setModalOpen(true);
  };

  // Handle modal close
  const closeModal = () => {
    setModalOpen(false);
    setModalData(null);
  };

  return (
    <div className="sorter-page">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFilesSelection(Array.from(e.target.files))}
      />
      <div className="sorter-container">
        {selectedImages.length === 0 ? (
          <div className="sorter-options">
            {/* Drop Zone Option - Single unified upload interface */}
            <div 
              className="sorter-option-card drop-zone-card" 
              onClick={handleDropZoneClick}
              onDragOver={handleDragOver}
              onDrop={handleDragDrop}
            >
              <div className="upload-placeholder">
                <div className="upload-icon">📷</div>
                <h3>Drop Images OR Click to Upload</h3>
                <p>JPG, PNG, WebP • Max 5MB</p>
              </div>
            </div>
          </div>
        ) : null}

        {selectedImages.length > 0 && (
          <div className="image-preview-section">
            <div className="images-grid">
              {selectedImages.map((img, index) => (
                <div key={index} className="preview-card">
                  <button className="remove-btn" onClick={() => handleRemoveImage(index)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                  <img src={img.preview} alt={`Selected ${index + 1}`} className="preview-image" />
                  <div className="preview-info">
                    <p className="file-name">{img.file?.name || `Photo ${index + 1}`}</p>
                    <p className="file-size">
                      {(img.file?.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="preview-actions">
              <button 
                className="btn-secondary" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Clear All button clicked!');
                  handleClearAll();
                }}
                style={{ touchAction: 'manipulation' }}
              >
                Clear All ({selectedImages.length})
              </button>
              <button 
                className="btn-primary" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Process button clicked!');
                  handleProcessImages();
                }}
                style={{ touchAction: 'manipulation' }}
              >
                Process {selectedImages.length} Image{selectedImages.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="results-section" ref={resultsRef}>
            <h2 className="results-title">Processing Results</h2>
            <div className="result-table-wrapper">
              <table className="result-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product Description</th>
                    <th>Potential Price</th>
                    <th>Authentication Score</th>
                    <th>Condition Score</th>
                    <th>Thrifttops Suggestion</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Display only the first image from each processed batch */}
                  {results.map((result, index) => (
                    <tr key={index} className="result-row">
                      <td className="image-cell" data-label="Image">
                        <img 
                          src={result.image} 
                          alt={result.fileName} 
                          className="result-thumbnail"
                        />
                      </td>
                      <td className="description-cell" data-label="Product Description">
                        <div className="description-text">{result.description}</div>
                        <div className="file-name-small">{result.fileName}</div>
                      </td>
                      <td className="price-cell" data-label="Potential Price">
                        <span className="price-value">${result.price}</span>
                      </td>
                      <td className="score-cell" data-label="Authentication Score">
                        <div className="score-with-icon">
                          <div className="score-badge score-auth">
                            {result.authScore}%
                          </div>
                          <button 
                            className="eye-icon-btn"
                            onClick={() => openReasonModal('Authentication', result.authScore, result.authReasons, result.description)}
                            title="View authentication details"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="score-cell" data-label="Condition Score">
                        <div className="score-with-icon">
                          <div className="score-badge score-condition">
                            {result.conditionScore}%
                          </div>
                          <button 
                            className="eye-icon-btn"
                            onClick={() => openReasonModal('Condition', result.conditionScore, result.conditionReasons, result.description)}
                            title="View condition details"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="suggestion-cell" data-label="Thriftlops Suggestion">
                        <span className="suggestion-text">{result.suggestion}</span>
                      </td>
                      <td className="actions-cell" data-label="Actions">
                        <div className="action-buttons">
                          <button 
                            className="btn-primary btn-sm"
                            onClick={() => handleSellOnline(index)}
                            title="Sell online"
                          >
                            Sell Online
                          </button>
                          <button 
                            className="btn-secondary btn-sm"
                            onClick={() => handlePushToRetail(index)}
                            title="Push to retail"
                          >
                            Push to Retail
                          </button>
                          <button 
                            className="btn-secondary btn-sm"
                            onClick={() => handleSellWholesale(index)}
                            title="Sell in wholesale"
                          >
                            Wholesale
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reason Modal */}
        {modalOpen && modalData && (
          <div className="reason-modal-overlay" onClick={closeModal}>
            <div className="reason-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title-row">
                  <div className="modal-title-section">
                    <h2 className="modal-product-title">{modalData.description}</h2>
                    <h3 className="modal-analysis-type">{modalData.type} Analysis</h3>
                  </div>
                  <div className="modal-score">
                    <span className="score-label">Score</span>
                    <span className={`score-value ${modalData.type === 'Authentication' ? 'score-auth' : 'score-condition'}`}>
                      {modalData.score}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="modal-body">
                <h4 className="reasons-heading">Analysis Details:</h4>
                <ul className="reasons-list">
                  {modalData.reasons && Array.isArray(modalData.reasons) && modalData.reasons.length > 0 ? (
                    modalData.reasons.map((reason, idx) => (
                      <li key={idx} className={`reason-item reason-${reason.status}`}>
                        <span className="reason-icon">
                          {reason.status === 'pass' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                              <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                          ) : reason.status === 'warning' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                              <line x1="12" y1="9" x2="12" y2="13"></line>
                              <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                          ) : ''}
                        </span>
                        <span className="reason-text">{reason.text}</span>
                      </li>
                    ))
                  ) : (
                    <li className="reason-item reason-pass">
                      <span className="reason-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                      </span>
                      <span className="reason-text">No detailed analysis available for this item. Please process the images again to generate detailed reasons.</span>
                    </li>
                  )}
                </ul>
              </div>
              <div className="modal-footer">
                <button className="modal-btn-close" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SorterPage;